import { useState, useRef, useEffect } from "react";
import { IoCaretDownSharp } from "react-icons/io5";
import "./FilterPanel.css";

export default function FilterPanel({
  filters,
  setFilters,
  services = [],
  selectedCountry,
  categories = [],
  totalBusinesses = 0
}) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleCategory = (catId) => {
    setFilters((prev) => {
      const exists = prev.categories.includes(catId);
      const newCategories = exists ? prev.categories.filter((c) => c !== catId) : [...prev.categories, catId];

      let newServices = prev.services;
      if (exists) {
        const servicesFromRemoved = services.filter(s => s.category_id === catId).map(s => s.id);
        newServices = newServices.filter(sid => !servicesFromRemoved.includes(sid));
      }

      return {
        ...prev,
        categories: newCategories,
        services: newServices,
      };
    });
  };

  const toggleService = (serviceId) => {
    setFilters((prev) => {
      const exists = prev.services.includes(serviceId);
      return {
        ...prev,
        services: exists ? prev.services.filter((s) => s !== serviceId) : [...prev.services, serviceId],
      };
    });
  };

  const resetFilters = () => {
    setFilters({
      ...filters,
      categories: [],
      services: [],
    });
  };

  const filteredCategories = categories
    .filter(c => c.name.toLowerCase().includes(searchInput.toLowerCase()));

  const availableServices = (filters.categories.length === 0)
    ? services
    : services.filter(s => filters.categories.includes(s.category_id));

  const filteredServices = availableServices.filter(s => s.name.toLowerCase().includes(searchInput.toLowerCase()));

  const catName = (id) => categories.find(c => c.id === id)?.name || "";

  const selectedCategoryNames = filters.categories.map(catName);

  const selectedServiceNames = filters.services.map(sid => {
    const s = services.find(x => x.id === sid);
    return s ? s.name : sid;
  });

  const INDIA_CITIES = [
    "All",
    "Mumbai",
    "Delhi",
    "Kolkata",
    "Chennai",
    "Bengaluru",
    "Hyderabad",
    "Ahmedabad",
    "Pune",
    "Surat",
    "Jaipur",
  ];

  const GERMANY_CITIES = ["Munich", "All"];

  const cityOptions =
    selectedCountry === "india" ? INDIA_CITIES : GERMANY_CITIES;

  useEffect(() => {
    if (
      selectedCountry === "india" &&
      !INDIA_CITIES.includes(filters.city)
    ) {
      setFilters(prev => ({ ...prev, city: "All" }));
    }

    if (
      selectedCountry !== "india" &&
      !GERMANY_CITIES.includes(filters.city)
    ) {
      setFilters(prev => ({ ...prev, city: "All" }));
    }
  }, [selectedCountry]);

  return (
    <div className="filters-box" ref={panelRef}>
      <div className="filter-item">
        <label className="filter-label">Business Categories</label>

        <div
          className="custom-dropdown_filter multi-select"
          onClick={() => setOpenDropdown(openDropdown === "categories" ? null : "categories")}
        >
          <div className="custom-dropdown-selected multi-input">
            <span className="placeholder-text">
              {selectedCategoryNames.length === 0
                ? "Select categories"
                : selectedCategoryNames.length === 1
                  ? selectedCategoryNames[0]
                  : `${selectedCategoryNames[0]} + ${selectedCategoryNames.length - 1} selected`}
            </span>
            <IoCaretDownSharp className={`dropdown-arrow ${openDropdown === "categories" ? "rotate" : ""}`} />
          </div>

          {openDropdown === "categories" && (
            <div className="custom-dropdown-options multi-options" onClick={(e) => e.stopPropagation()}>
              <div className="search-box">
                <input
                  className="multi-select-input"
                  type="text"
                  placeholder="Search categories"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <div className="options-scroll">
                {[...filteredCategories].map((cat, idx) => (
                  <div className={`custom-dropdown-option ${filters.categories.includes(cat.id) ? "selected" : ""}`} key={cat.id || idx}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={filters.categories.includes(cat.id)}
                        onChange={() => toggleCategory(cat.id)}
                      />
                      <span>{cat.name}</span>
                    </label>
                  </div>
                ))}

                {filteredCategories.length === 0 && (
                  <div className="custom-dropdown-option">No categories available</div>
                )}
              </div>

              {filters.categories.length > 0 && (
                <div className="dropdown-bottom-bar">
                  <span className="job-count-info">{totalBusinesses} businesses available</span>
                  <button className="reset-btn" onClick={resetFilters}>Reset Filters</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="filter-item">
        <label className="filter-label">Business Services</label>

        <div
          className="custom-dropdown_filter multi-select"
          onClick={() => setOpenDropdown(openDropdown === "services" ? null : "services")}
        >
          <div className="custom-dropdown-selected multi-input">
            <span className="placeholder-text">
              {selectedServiceNames.length === 0
                ? "Select services"
                : selectedServiceNames.length === 1
                  ? selectedServiceNames[0]
                  : `${selectedServiceNames[0]} + ${selectedServiceNames.length - 1} selected`}
            </span>
            <IoCaretDownSharp className={`dropdown-arrow ${openDropdown === "services" ? "rotate" : ""}`} />
          </div>

          {openDropdown === "services" && (
            <div className="custom-dropdown-options multi-options" onClick={(e) => e.stopPropagation()}>
              <div className="search-box">
                <input
                  className="multi-select-input"
                  type="text"
                  placeholder="Search services"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <div className="options-scroll">
                {[...filteredServices].map((serv, idx) => (
                  <div className={`custom-dropdown-option ${filters.services.includes(serv.id) ? "selected" : ""}`} key={serv.id || idx}>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={filters.services.includes(serv.id)}
                        onChange={() => toggleService(serv.id)}
                      />
                      <span>{serv.name}</span>
                      <small style={{ display: 'block', color: '#888' }}>{categories.find(c => c.id === serv.category_id)?.name}</small>
                    </label>
                  </div>
                ))}

                {filteredServices.length === 0 && (
                  <div className="custom-dropdown-option">No services available</div>
                )}
              </div>

              {filters.services.length > 0 && (
                <div className="dropdown-bottom-bar">
                  <span className="job-count-info">{totalBusinesses} businesses available</span>
                  <button
                    className="reset-btn"
                    onClick={() => setFilters({ ...filters, services: [] })}
                  >
                    Reset Services
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="filter-item">
        <label className="filter-label">City</label>

        <div
          className="custom-dropdown_filter"
          onClick={() =>
            setOpenDropdown(openDropdown === "city" ? null : "city")
          }
        >
          <div className="custom-dropdown-selected">
            {filters.city || "All"}
            <IoCaretDownSharp
              className={`dropdown-arrow ${openDropdown === "city" ? "rotate" : ""}`}
            />
          </div>

          {openDropdown === "city" && (
            <div className="custom-dropdown-options">
              {cityOptions.map((option, idx) => (
                <div
                  key={idx}
                  className={`custom-dropdown-option ${filters.city === option ? "selected" : ""
                    }`}
                  onClick={() => {
                    setFilters({ ...filters, city: option });
                    setOpenDropdown(null);
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
